import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type {
  Lead,
  ScoringResult,
  MarketComparison,
  FinancialProjection,
  Answer
} from '@/types';
import type { ProductConfig } from '@/types/product';
import { APP_CONFIG } from '@/config/constants';
import { formatCurrencyCompact, formatDate } from '@/utils/formatters';
import { formatAnswerForDisplay } from '../questionnaire/answer-processor';

interface PdfGeneratorOptions {
  lead: Lead;
  scoring: ScoringResult;
  marketComparison: MarketComparison[];
  financialProjection: FinancialProjection | null;
  answers: Answer[];
  productConfig: ProductConfig;
  logoBase64?: string;
}

export class PdfGenerator {
  private doc: jsPDF;
  private options: PdfGeneratorOptions;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number = 20;
  private logoBase64: string | null = null;
  private googleLogoBase64: string | null = null;
  private companyName: string;

  constructor(options: PdfGeneratorOptions) {
    this.options = options;
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.companyName = this.getCompanyNameFromAnswers();
  }

  private getCompanyNameFromAnswers(): string {
    const { productConfig, answers, lead } = this.options;
    if (productConfig.companyNameQuestionId) {
      const answer = answers.find(a => a.questionId === productConfig.companyNameQuestionId);
      if (answer && typeof answer.value === 'string' && answer.value.trim()) {
        return answer.value.trim();
      }
    }
    return lead.nome;
  }

  async generate(): Promise<Blob> {
    await this.loadLogo();
    await this.loadGoogleLogo();
    await this.addCoverPage();
    this.addScorePage();
    this.addStatusPage();

    const { pdf } = this.options.productConfig;
    if (pdf === 'franqueabilidade') {
      this.addMarketComparisonPage();
      if (this.options.financialProjection) {
        this.addProjectionsPage();
      }
    } else if (pdf === 'select') {
      this.addSelectProfilePage();
    } else if (pdf === 'expansao') {
      this.addExpansaoProfilePage();
    }

    this.addQuestionnairePage();
    this.addInovaPage();

    return this.doc.output('blob');
  }

  private async loadLogo(): Promise<void> {
    try {
      const response = await fetch('/assets/inova-hub-franquias.png');
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.logoBase64 = reader.result as string;
          resolve();
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Could not load logo:', error);
    }
  }

  private async loadGoogleLogo(): Promise<void> {
    try {
      const response = await fetch('/assets/logo_google.png');
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.googleLogoBase64 = reader.result as string;
          resolve();
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Could not load Google logo:', error);
    }
  }

  private addLogo(x: number, y: number, width: number): void {
    if (this.logoBase64) {
      try {
        this.doc.addImage(this.logoBase64, 'PNG', x, y, width, width * 0.4);
      } catch {
        this.doc.setFontSize(14);
        this.doc.setFont('helvetica', 'bold');
        this.doc.text('INOVA FRANQUIAS', x, y + 10);
      }
    }
  }

  private async addCoverPage(): Promise<void> {
    const gradientSteps = 50;
    const stepHeight = this.pageHeight / gradientSteps;

    for (let i = 0; i < gradientSteps; i++) {
      const grayValue = Math.round(240 - (i * 30 / gradientSteps));
      this.doc.setFillColor(grayValue, grayValue, grayValue);
      this.doc.rect(0, i * stepHeight, this.pageWidth, stepHeight + 1, 'F');
    }

    if (this.logoBase64) {
      this.addLogo(this.pageWidth / 2 - 30, 30, 60);
    }

    this.doc.setTextColor(45, 55, 72);
    this.doc.setFontSize(28);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(this.options.productConfig.title, this.pageWidth / 2, 100, { align: 'center' });

    this.doc.setFontSize(22);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(60, 60, 60);
    this.doc.text(this.companyName, this.pageWidth / 2, 120, { align: 'center' });

    const { scoring } = this.options;
    this.doc.setFontSize(48);
    this.doc.setFont('helvetica', 'bold');
    const scoreColor = this.getScoreColor(scoring.totalPercentage);
    this.doc.setTextColor(scoreColor.r, scoreColor.g, scoreColor.b);
    this.doc.text(`${scoring.totalPercentage}%`, this.pageWidth / 2, 160, { align: 'center' });

    this.doc.setFontSize(14);
    this.doc.setTextColor(100, 100, 100);
    this.doc.text('Score do Diagnóstico', this.pageWidth / 2, 175, { align: 'center' });

    this.doc.setFontSize(11);
    this.doc.setTextColor(80, 80, 80);
    const description = this.options.productConfig.description;
    const words = description.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    for (const word of words) {
      const test = currentLine ? `${currentLine} ${word}` : word;
      if (test.length > 60) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = test;
      }
    }
    if (currentLine) lines.push(currentLine);

    let yPos = 200;
    for (const line of lines) {
      this.doc.text(line, this.pageWidth / 2, yPos, { align: 'center' });
      yPos += 7;
    }

    this.doc.setFontSize(12);
    this.doc.setTextColor(45, 55, 72);
    this.doc.text(APP_CONFIG.COMPANY_NAME, this.pageWidth / 2, this.pageHeight - 20, { align: 'center' });

    this.doc.setFontSize(10);
    this.doc.setTextColor(100, 100, 100);
    this.doc.text(`Data: ${formatDate(new Date())}`, this.pageWidth / 2, this.pageHeight - 12, { align: 'center' });
  }

  private addScorePage(): void {
    this.doc.addPage();
    this.addHeader('Score do Diagnóstico');

    const { scoring, productConfig } = this.options;
    const centerX = this.pageWidth / 2;
    const { nonScoringCategories, labels } = productConfig.scoring;

    this.doc.setFontSize(14);
    this.doc.setTextColor(60, 60, 60);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Situação Atual', centerX, 50, { align: 'center' });

    const scoringCategories = scoring.categoryScores.filter(
      c => !nonScoringCategories.includes(c.category) || productConfig.scoring.weights[c.category] > 0
    );
    const radarLabels = scoringCategories.map(c =>
      this.shortenLabel(labels[c.category] ?? c.category)
    );

    this.drawRadarChart(scoringCategories, centerX, 95, 35, [52, 152, 219], radarLabels);

    this.doc.setFontSize(24);
    this.doc.setFont('helvetica', 'bold');
    const scoreColor = this.getScoreColor(scoring.totalPercentage);
    this.doc.setTextColor(scoreColor.r, scoreColor.g, scoreColor.b);
    this.doc.text(`${scoring.totalPercentage}%`, centerX, 145, { align: 'center' });

    this.doc.setFontSize(10);
    this.doc.setTextColor(100, 100, 100);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(scoring.finalRecommendation, centerX, 153, { align: 'center' });

    this.doc.setFontSize(14);
    this.doc.setTextColor(60, 60, 60);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Projeção Após Inova Franquias', centerX, 175, { align: 'center' });

    const projectedScores = scoringCategories.map(c => {
      const maxPoints = productConfig.scoring.weights[c.category] ?? 0;
      if (maxPoints === 0) return { ...c, percentage: c.percentage };
      const improvementFactor = productConfig.scoring.improvements[c.category] ?? 1.0;
      const improved = maxPoints * improvementFactor;
      const projectedEarned = Math.max(c.earnedPoints, improved);
      const projectedPct = Math.round((projectedEarned / maxPoints) * 100);
      return { ...c, percentage: Math.min(projectedPct, 100) };
    });
    this.drawRadarChart(projectedScores, centerX, 220, 35, [34, 139, 34], radarLabels);

    this.doc.setFontSize(24);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(34, 139, 34);
    this.doc.text(`${scoring.projectedScoreAfterInova}%`, centerX, 270, { align: 'center' });

    this.doc.setFontSize(10);
    this.doc.setTextColor(100, 100, 100);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Score projetado após consultoria', centerX, 278, { align: 'center' });
  }

  private shortenLabel(label: string): string {
    const shortenings: Record<string, string> = {
      'Credibilidade da Marca': 'Credibilidade',
      'Modelo de Negócio e Padronização': 'Processos',
      'Perfil do Franqueador': 'Perf. Franq.',
      'Diferenciais Competitivos': 'Diferenciais',
      'Capacidade de Expansão': 'Expansão',
      'Registro de Marca': 'Reg. Marca',
      'Capacidade Financeira': 'Cap. Financeira',
      'Prontidão para Investir': 'Prontidão',
      'Direcionamento de Mercado': 'Direcionamento',
      'Estrutura da Rede': 'Estrutura',
      'Capacidade de Investimento': 'Investimento',
      'Prontidão para Expansão': 'Prontidão',
      'Indicadores Financeiros': 'Ind. Financeiro'
    };
    return shortenings[label] ?? label.substring(0, 12);
  }

  private drawRadarChart(
    categoryScores: { percentage: number }[],
    centerX: number,
    centerY: number,
    radius: number,
    color: [number, number, number],
    labels: string[]
  ): void {
    const categories = categoryScores.length;
    const angleStep = (2 * Math.PI) / categories;

    this.doc.setDrawColor(220, 220, 220);
    this.doc.setLineWidth(0.2);

    for (let level = 1; level <= 5; level++) {
      const levelRadius = (radius * level) / 5;
      const points: { x: number; y: number }[] = [];

      for (let i = 0; i < categories; i++) {
        const angle = i * angleStep - Math.PI / 2;
        points.push({
          x: centerX + levelRadius * Math.cos(angle),
          y: centerY + levelRadius * Math.sin(angle)
        });
      }

      for (let i = 0; i < points.length; i++) {
        const next = points[(i + 1) % points.length];
        this.doc.line(points[i].x, points[i].y, next.x, next.y);
      }
    }

    for (let i = 0; i < categories; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      this.doc.line(centerX, centerY, x, y);
    }

    this.doc.setDrawColor(color[0], color[1], color[2]);
    this.doc.setLineWidth(1.5);

    const dataPoints: { x: number; y: number }[] = [];
    for (let i = 0; i < categories; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const value = Math.max(categoryScores[i].percentage / 100, 0.1);
      const r = radius * value;
      dataPoints.push({
        x: centerX + r * Math.cos(angle),
        y: centerY + r * Math.sin(angle)
      });
    }

    for (let i = 0; i < dataPoints.length; i++) {
      const next = dataPoints[(i + 1) % dataPoints.length];
      this.doc.line(dataPoints[i].x, dataPoints[i].y, next.x, next.y);
    }

    this.doc.setFillColor(color[0], color[1], color[2]);
    for (const point of dataPoints) {
      this.doc.circle(point.x, point.y, 1.5, 'F');
    }

    this.doc.setFontSize(7);
    this.doc.setTextColor(80, 80, 80);
    this.doc.setFont('helvetica', 'normal');

    for (let i = 0; i < categories; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const labelRadius = radius + 8;
      const x = centerX + labelRadius * Math.cos(angle);
      const y = centerY + labelRadius * Math.sin(angle);

      let align: 'left' | 'center' | 'right' = 'center';
      let offsetX = 0;

      if (Math.cos(angle) < -0.3) {
        align = 'right';
        offsetX = -2;
      } else if (Math.cos(angle) > 0.3) {
        align = 'left';
        offsetX = 2;
      }

      this.doc.text(labels[i] ?? '', x + offsetX, y + 1, { align });
    }
  }

  private addStatusPage(): void {
    this.doc.addPage();
    this.addHeader('Status do Diagnóstico');

    const { scoring, productConfig } = this.options;
    const { nonScoringCategories } = productConfig.scoring;

    const tableData = scoring.categoryScores
      .filter(c => !nonScoringCategories.includes(c.category))
      .map(c => {
        const statusIcon = c.status === 'ok' ? 'OK' : c.status === 'attention' ? 'ATENÇÃO' : 'CRÍTICO';
        const difficultyLabel = c.difficulty === 'none' ? '-' :
          c.difficulty === 'easy' ? 'Fácil' :
          c.difficulty === 'moderate' ? 'Moderado' : 'Difícil';

        return [
          c.label,
          `${c.percentage}%`,
          statusIcon,
          c.recommendation,
          difficultyLabel
        ];
      });

    autoTable(this.doc, {
      startY: 45,
      head: [['Critério', '%', 'Status', 'Recomendação', 'Dificuldade']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 12, halign: 'center' },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 80 },
        4: { cellWidth: 22, halign: 'center' }
      },
      didParseCell: (data) => {
        if (data.column.index === 2 && data.section === 'body') {
          const status = data.cell.raw as string;
          if (status === 'OK') {
            data.cell.styles.textColor = [34, 139, 34];
            data.cell.styles.fontStyle = 'bold';
          } else if (status === 'ATENÇÃO') {
            data.cell.styles.textColor = [255, 140, 0];
            data.cell.styles.fontStyle = 'bold';
          } else if (status === 'CRÍTICO') {
            data.cell.styles.textColor = [220, 53, 69];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    const finalY = (this.doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || 150;

    this.doc.setFontSize(9);
    this.doc.setTextColor(80, 80, 80);
    this.doc.text('Legenda:', this.margin, finalY + 15);

    const legendY = finalY + 23;

    this.doc.setFillColor(34, 139, 34);
    this.doc.circle(this.margin + 5, legendY, 2, 'F');
    this.doc.setFontSize(8);
    this.doc.text('OK - Atendido', this.margin + 10, legendY + 1);

    this.doc.setFillColor(255, 140, 0);
    this.doc.circle(this.margin + 45, legendY, 2, 'F');
    this.doc.text('Atenção - Melhorar', this.margin + 50, legendY + 1);

    this.doc.setFillColor(220, 53, 69);
    this.doc.circle(this.margin + 95, legendY, 2, 'F');
    this.doc.text('Crítico - Urgente', this.margin + 100, legendY + 1);
  }

  private addMarketComparisonPage(): void {
    this.doc.addPage();
    this.addHeader('Comparativo com o Mercado');

    const { marketComparison } = this.options;

    const tableData = marketComparison.map(c => {
      const insightLabel =
        c.insight === 'above_average' ? '↑ Acima' :
        c.insight === 'within_expected' ? '= Média' : '↓ Abaixo';

      return [
        c.indicator,
        String(c.marketAverage),
        String(c.companyValue),
        insightLabel
      ];
    });

    autoTable(this.doc, {
      startY: 45,
      head: [['Indicador', 'Média do Mercado', 'Sua Empresa', 'Posição']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 40, halign: 'center' },
        2: { cellWidth: 40, halign: 'center' },
        3: { cellWidth: 30, halign: 'center' }
      },
      didParseCell: (data) => {
        if (data.column.index === 3 && data.section === 'body') {
          const position = data.cell.raw as string;
          if (position.includes('Acima')) {
            data.cell.styles.textColor = [34, 139, 34];
            data.cell.styles.fontStyle = 'bold';
          } else if (position.includes('Abaixo')) {
            data.cell.styles.textColor = [220, 53, 69];
            data.cell.styles.fontStyle = 'bold';
          } else if (position.includes('Média')) {
            data.cell.styles.textColor = [255, 140, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    const finalY = (this.doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || 150;

    this.doc.setFontSize(9);
    this.doc.setTextColor(100, 100, 100);
    this.doc.text(
      'Dados baseados em pesquisa de mercado do setor de franquias no Brasil.',
      this.margin,
      finalY + 15
    );
  }

  private addProjectionsPage(): void {
    if (!this.options.financialProjection) return;

    this.doc.addPage();
    this.addHeader('Projeções Financeiras - 5 Anos');

    const { financialProjection } = this.options;

    this.doc.setFontSize(12);
    this.doc.setTextColor(60, 60, 60);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Crescimento Projetado da Rede', this.margin, 50);

    let yPos = 58;

    // Calcular running total acumulado de unidades
    let runningUnitsTotal = 0;
    const cumulativeUnitsList: number[] = financialProjection.projections.map(p => {
      runningUnitsTotal += p.unitsCount;
      return runningUnitsTotal;
    });

    const maxCumulativeUnits = cumulativeUnitsList[cumulativeUnitsList.length - 1];
    const maxBarWidth = 100;

    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);

    for (let i = 0; i < financialProjection.projections.length; i++) {
      const proj = financialProjection.projections[i];
      const cumUnits = cumulativeUnitsList[i];
      const barWidth = (cumUnits / maxCumulativeUnits) * maxBarWidth;

      this.doc.setFillColor(52, 152, 219);
      this.doc.roundedRect(this.margin, yPos, barWidth, 7, 1, 1, 'F');

      this.doc.setTextColor(60, 60, 60);
      this.doc.text(`Ano ${proj.year}: ${cumUnits} unidades acumuladas`, this.margin + barWidth + 5, yPos + 5);

      yPos += 11;
    }

    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Projeção de Receitas', this.margin, yPos + 12);

    const tableData = financialProjection.projections.map((p, i) => [
      `Ano ${p.year}`,
      String(p.unitsCount),
      String(cumulativeUnitsList[i]),
      formatCurrencyCompact(p.grossRevenue),
      formatCurrencyCompact(p.taxes),
      formatCurrencyCompact(p.grossProfit),
      formatCurrencyCompact(p.fixedCosts),
      formatCurrencyCompact(p.netProfit),
      formatCurrencyCompact(p.cumulativeNetProfit)
    ]);

    autoTable(this.doc, {
      startY: yPos + 18,
      margin: { left: this.margin, right: this.margin },
      head: [['Período', 'Unid.', 'Unid. Acum.', 'Receita Bruta', 'Impostos', 'Lucro Bruto', 'Custos Fixos', 'Lucro Líq.', 'Lucro Acum.']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
        fontSize: 6,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 6,
        overflow: 'hidden'
      },
      columnStyles: {
        0: { cellWidth: 13 },
        1: { cellWidth: 9, halign: 'center' },
        2: { cellWidth: 13, halign: 'center' },
        3: { cellWidth: 27, halign: 'right' },
        4: { cellWidth: 22, halign: 'right' },
        5: { cellWidth: 25, halign: 'right' },
        6: { cellWidth: 22, halign: 'right' },
        7: { cellWidth: 25, halign: 'right' },
        8: { cellWidth: 27, halign: 'right' }
      },
      didParseCell: (data) => {
        if (data.column.index === 8 && data.section === 'body') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [34, 139, 34];
        }
        if (data.column.index === 2 && data.section === 'body') {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    const tableY = (this.doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || 200;

    this.doc.setFontSize(8);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(100, 100, 100);
    this.doc.text(`Taxa de crescimento anual estimada: ${(financialProjection.growthRate * 100)}%`, this.margin, tableY + 8);
    this.doc.text(
      `Impostos: ~${financialProjection.averageRepresentation.taxes}% | Custos Fixos: ~${financialProjection.averageRepresentation.fixedCosts}% | Marketing: ~${financialProjection.averageRepresentation.marketing}%`,
      this.margin,
      tableY + 14
    );
  }

  private addSelectProfilePage(): void {
    this.doc.addPage();
    this.addHeader('Seu Perfil de Investidor');

    const { scoring, answers } = this.options;
    const totalPercentage = scoring.totalPercentage;

    const getAnswer = (id: string): string => {
      const a = answers.find(ans => ans.questionId === id);
      if (!a) return 'Não informado';
      if (typeof a.value === 'string') return a.value;
      return String(a.value);
    };

    const getAnswerLabel = (id: string, questions: Array<{ id: string; options?: Array<{ value: string; label: string }> }>): string => {
      const a = answers.find(ans => ans.questionId === id);
      if (!a) return 'Não informado';
      const q = questions.find(q => q.id === id);
      if (q?.options) {
        const opt = q.options.find(o => o.value === String(a.value));
        if (opt) return opt.label;
      }
      return String(a.value);
    };

    const productQuestions = this.options.productConfig.questions;
    let yPos = 45;

    // Block 1: Capacidade Financeira
    this.drawProfileBlock('Capacidade Financeira', [
      { label: 'Capital disponível', value: getAnswerLabel('sel_cap_a', productQuestions) },
      { label: 'Disponibilidade imediata', value: getAnswerLabel('sel_cap_b', productQuestions) },
      { label: 'Reserva emergencial', value: getAnswerLabel('sel_cap_c', productQuestions) }
    ], yPos);
    yPos += 55;

    // Block 2: Prontidão para o Investimento
    this.drawProfileBlock('Prontidão para o Investimento', [
      { label: 'Prazo para investir', value: getAnswerLabel('sel_pron_d', productQuestions) },
      { label: 'Experiência empresarial', value: getAnswerLabel('sel_pron_e', productQuestions) },
      { label: 'Dedicação ao negócio', value: getAnswerLabel('sel_pron_f', productQuestions) }
    ], yPos);
    yPos += 55;

    // Block 3: Direcionamento de Mercado
    this.drawProfileBlock('Direcionamento de Mercado', [
      { label: 'Segmento de afinidade', value: getAnswerLabel('sel_dir_g', productQuestions) },
      { label: 'Experiência no segmento', value: getAnswerLabel('sel_dir_h', productQuestions) },
      { label: 'Objetivos e expectativas', value: getAnswer('sel_dir_i').substring(0, 80) + (getAnswer('sel_dir_i').length > 80 ? '...' : '') }
    ], yPos);
    yPos += 65;

    // Conclusive text
    const conclusionText = totalPercentage >= 70
      ? 'Você tem o perfil e os recursos para investir com segurança e tranquilidade.'
      : totalPercentage >= 40
      ? 'Você está no caminho certo. Alguns ajustes garantirão mais segurança ao investir.'
      : 'Recomendamos fortalecer sua base financeira antes de avançar no investimento.';

    const bgColor = totalPercentage >= 70 ? [34, 139, 34] : totalPercentage >= 40 ? [255, 140, 0] : [220, 53, 69];
    this.doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
    this.doc.roundedRect(this.margin, yPos, this.pageWidth - 2 * this.margin, 20, 3, 3, 'F');

    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(conclusionText, this.pageWidth / 2, yPos + 13, { align: 'center' });
  }

  private addExpansaoProfilePage(): void {
    this.doc.addPage();
    this.addHeader('Potencial de Expansão da Sua Rede');

    const { scoring, answers } = this.options;
    const totalPercentage = scoring.totalPercentage;

    const getAnswerLabel = (id: string, questions: Array<{ id: string; options?: Array<{ value: string; label: string }> }>): string => {
      const a = answers.find(ans => ans.questionId === id);
      if (!a) return 'Não informado';
      const q = questions.find(q => q.id === id);
      if (q?.options) {
        const opt = q.options.find(o => o.value === String(a.value));
        if (opt) return opt.label;
      }
      return String(a.value);
    };

    const getAnswerRaw = (id: string): string => {
      const a = answers.find(ans => ans.questionId === id);
      if (!a) return 'Não informado';
      if (typeof a.value === 'string') return a.value;
      return String(a.value);
    };

    const productQuestions = this.options.productConfig.questions;
    let yPos = 45;

    // Block 1: Maturidade da Rede
    this.drawProfileBlock('Maturidade da Rede', [
      { label: 'Marca da franquia', value: getAnswerRaw('exp_est_a') },
      { label: 'Presença digital (site)', value: getAnswerRaw('exp_est_b') },
      { label: 'Unidades franqueadas', value: getAnswerLabel('exp_est_c', productQuestions) }
    ], yPos);
    yPos += 55;

    // Block 2: Capacidade de Geração de Demanda
    this.drawProfileBlock('Capacidade de Geração de Demanda', [
      { label: 'Budget tráfego pago', value: getAnswerLabel('exp_inv_d', productQuestions) },
      { label: 'Orçamento de marketing', value: getAnswerRaw('exp_inv_e') },
      { label: 'Taxa de franquia', value: getAnswerLabel('exp_inv_f', productQuestions) }
    ], yPos);
    yPos += 55;

    // Block 3: Prontidão Comercial
    this.drawProfileBlock('Prontidão Comercial', [
      { label: 'Prazo de início', value: getAnswerLabel('exp_pron_g', productQuestions) },
      { label: 'Processo estruturado', value: getAnswerLabel('exp_pron_h', productQuestions) },
      { label: 'Leads recorrentes', value: getAnswerLabel('exp_pron_i', productQuestions) }
    ], yPos);
    yPos += 65;

    // Conclusive text
    const conclusionText = totalPercentage >= 70
      ? 'Sua rede tem estrutura e recursos para uma expansão expressiva com a Inova Franquias.'
      : totalPercentage >= 40
      ? 'Há potencial real de expansão. A consultoria certa vai acelerar seus resultados.'
      : 'Sua rede precisa consolidar alguns pilares antes de escalar a expansão.';

    const bgColor = totalPercentage >= 70 ? [34, 139, 34] : totalPercentage >= 40 ? [255, 140, 0] : [220, 53, 69];
    this.doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
    this.doc.roundedRect(this.margin, yPos, this.pageWidth - 2 * this.margin, 20, 3, 3, 'F');

    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(conclusionText, this.pageWidth / 2, yPos + 13, { align: 'center' });
  }

  private drawProfileBlock(
    title: string,
    items: Array<{ label: string; value: string }>,
    yPos: number
  ): void {
    // Section header
    this.doc.setFillColor(52, 73, 94);
    this.doc.roundedRect(this.margin, yPos, this.pageWidth - 2 * this.margin, 10, 2, 2, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.margin + 5, yPos + 7);

    // Items
    let itemY = yPos + 14;
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);

    for (const item of items) {
      this.doc.setTextColor(80, 80, 80);
      this.doc.text(`${item.label}:`, this.margin + 3, itemY);
      this.doc.setTextColor(40, 40, 40);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(item.value, this.margin + 55, itemY);
      this.doc.setFont('helvetica', 'normal');
      itemY += 9;
    }
  }


  private addQuestionnairePage(): void {
    this.doc.addPage();
    this.addHeader('Respostas do Questionário');

    const { answers, productConfig } = this.options;

    const tableData: string[][] = [];
    const rowMeta: { score: number; maxScore: number; questionId: string; numericValue: number | null }[] = [];

    for (const answer of answers) {
      const question = productConfig.questions.find(q => q.id === answer.questionId);
      if (question) {
        const formattedAnswer = formatAnswerForDisplay(question, answer);
        tableData.push([question.text, formattedAnswer]);
        rowMeta.push({
          score: answer.score,
          maxScore: question.maxScore,
          questionId: question.id,
          numericValue: typeof answer.value === 'number' ? answer.value : null
        });
      }
    }

    autoTable(this.doc, {
      startY: 45,
      head: [['Pergunta', 'Resposta']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: 115 },
        1: { cellWidth: 55 }
      },
      didParseCell: (data) => {
        if (data.column.index === 1 && data.section === 'body') {
          const meta = rowMeta[data.row.index];
          if (!meta) return;

          // fin_net_revenue: color by value (>=100k green, <100k orange)
          if (meta.questionId === 'fin_net_revenue' && meta.numericValue !== null) {
            data.cell.styles.textColor = meta.numericValue >= 100000
              ? [34, 139, 34]
              : [255, 140, 0];
            data.cell.styles.fontStyle = 'bold';
            return;
          }

          // Scored questions: color by score (except dif_differentials)
          if (meta.maxScore > 0 && meta.questionId !== 'dif_differentials') {
            if (meta.score > 8) {
              data.cell.styles.textColor = [34, 139, 34];
            } else if (meta.score >= 5) {
              data.cell.styles.textColor = [255, 140, 0];
            } else {
              data.cell.styles.textColor = [220, 53, 69];
            }
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) {
          this.addHeader('Respostas do Questionário (cont.)');
        }
      }
    });
  }

  private addInovaPage(): void {
    this.doc.addPage();

    const gradientSteps = 50;
    const stepHeight = this.pageHeight / gradientSteps;

    for (let i = 0; i < gradientSteps; i++) {
      const grayValue = Math.round(240 - (i * 30 / gradientSteps));
      this.doc.setFillColor(grayValue, grayValue, grayValue);
      this.doc.rect(0, i * stepHeight, this.pageWidth, stepHeight + 1, 'F');
    }

    if (this.logoBase64) {
      this.addLogo(this.pageWidth / 2 - 35, 40, 70);
    }

    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(80, 80, 80);
    this.doc.text('Transformando negócios em franquias de sucesso', this.pageWidth / 2, 95, { align: 'center' });

    const statsY = 120;
    this.doc.setFillColor(52, 73, 94);
    this.doc.roundedRect(this.margin, statsY, this.pageWidth - 2 * this.margin, 80, 5, 5, 'F');

    this.doc.setTextColor(52, 152, 219);
    this.doc.setFontSize(36);
    this.doc.setFont('helvetica', 'bold');

    const statCenters = [
      this.margin + 45,
      this.pageWidth / 2,
      this.pageWidth - this.margin - 45
    ];

    this.doc.text('+700', statCenters[0], statsY + 30, { align: 'center' });
    this.doc.setFontSize(10);
    this.doc.setTextColor(200, 200, 200);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Marcas', statCenters[0], statsY + 42, { align: 'center' });
    this.doc.text('Formatadas', statCenters[0], statsY + 50, { align: 'center' });

    this.doc.setTextColor(52, 152, 219);
    this.doc.setFontSize(36);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('+4000', statCenters[1], statsY + 30, { align: 'center' });
    this.doc.setFontSize(10);
    this.doc.setTextColor(200, 200, 200);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Unidades', statCenters[1], statsY + 42, { align: 'center' });
    this.doc.text('Vendidas', statCenters[1], statsY + 50, { align: 'center' });

    this.doc.setTextColor(52, 152, 219);
    this.doc.setFontSize(36);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('+10', statCenters[2], statsY + 30, { align: 'center' });
    this.doc.setFontSize(10);
    this.doc.setTextColor(200, 200, 200);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('Anos no', statCenters[2], statsY + 42, { align: 'center' });
    this.doc.text('Brasil', statCenters[2], statsY + 50, { align: 'center' });

    // Google rating badge
    const badgeY = statsY + 58;
    this.doc.setFillColor(34, 139, 34);
    this.doc.roundedRect(this.pageWidth / 2 - 65, badgeY, 130, 18, 4, 4, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Nota Maxima 5/5 no Google  |  * * * * *', this.pageWidth / 2, badgeY + 11, { align: 'center' });

    // Google logo below badge
    const logoY = badgeY + 24;
    if (this.googleLogoBase64) {
      try {
        const logoW = 28;
        const logoH = 10;
        this.doc.addImage(this.googleLogoBase64, 'PNG', this.pageWidth / 2 - logoW / 2, logoY, logoW, logoH);
      } catch {
        // ignore if google logo fails
      }
    }

    this.doc.setTextColor(60, 60, 60);
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'normal');

    const descLines = [
      'A Inova Franquias é especialista em transformar negócios de sucesso',
      'em redes de franquias prósperas. Com metodologia própria e equipe',
      'altamente qualificada, ajudamos empresários a expandir suas marcas',
      'de forma estruturada e sustentável em todo o Brasil.'
    ];

    let descY = 248;
    for (const line of descLines) {
      this.doc.text(line, this.pageWidth / 2, descY, { align: 'center' });
      descY += 8;
    }

    this.doc.setTextColor(52, 152, 219);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text('www.inovafranquias.com.br', this.pageWidth / 2, this.pageHeight - 12, { align: 'center' });
  }

  private addHeader(title: string): void {
    const w = this.doc.internal.pageSize.getWidth();
    this.doc.setFillColor(52, 73, 94);
    this.doc.rect(0, 0, w, 30, 'F');

    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.margin, 19);

    if (this.logoBase64) {
      try {
        this.doc.addImage(this.logoBase64, 'PNG', w - this.margin - 40, 5, 35, 14);
      } catch {
        // Ignore if logo fails
      }
    }
  }

  private getScoreColor(percentage: number): { r: number; g: number; b: number } {
    if (percentage >= 70) return { r: 34, g: 139, b: 34 };
    if (percentage >= 40) return { r: 255, g: 140, b: 0 };
    return { r: 220, g: 53, b: 69 };
  }
}

export async function generateDiagnosticPdf(options: PdfGeneratorOptions): Promise<Blob> {
  const generator = new PdfGenerator(options);
  return generator.generate();
}

export function downloadPdf(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
